import {environment} from '@root/src/environments/environment';

declare var ol: any;

export type OldMapBaseLayer = 'plan' | 'google' | 'sentinel' | 'landsat';

/** XYZ URL templates for the satellite base layers (OpenLayers uses {x}/{y}/{z}). */
const OLD_MAP_SATELLITE_URLS: Record<'google' | 'sentinel' | 'landsat', string> = {
  google: 'https://mt{0-3}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
  sentinel: environment.api + '/tiles/Map/GetTileByLayer?x={x}&y={y}&z={z}&layer=Sentinel2_2026&coordinateSystem=0',
  landsat: environment.api + '/tiles/Map/GetTileByLayer?x={x}&y={y}&z={z}&layer=Landsat_2026&coordinateSystem=0',
};

export class MapManager {
  private map: any;
  private vectorSource: any;
  private vectorLayer: any;
  private satelliteLayer: any;
  private planLayer: any;
  private _isSatelliteLayer: boolean = true;
  private centerMarker: any;

  constructor(
    private mapContainer: HTMLElement,
    private center: [number, number],
    private zoom: number
  ) {}

  get mapInstance(): any {
    return this.map;
  }

  get vectorSourceInstance(): any {
    return this.vectorSource;
  }

  get vectorLayerInstance(): any {
    return this.vectorLayer;
  }

  get satelliteLayerInstance(): any {
    return this.satelliteLayer;
  }

  get planLayerInstance(): any {
    return this.planLayer;
  }

  get isSatelliteLayer(): boolean {
    return this._isSatelliteLayer;
  }

  initVectorLayer(): void {
    this.vectorSource = new ol.source.Vector();
    this.vectorLayer = new ol.layer.Vector({
      source: this.vectorSource,
      style: new ol.style.Style({
        fill: new ol.style.Fill({
          color: 'rgba(119, 255, 15, 0.34)'
        }),
        stroke: new ol.style.Stroke({
          color: '#ff4b4b',
          width: 2
        }),
        image: new ol.style.Circle({
          radius: 5,
          fill: new ol.style.Fill({
            color: '#ff0000'
          }),
          stroke: new ol.style.Stroke({
            color: '#ffffff',
            width: 2
          })
        })
      })
    });
  }

  initializeMap(): boolean {
    if (!this.mapContainer || this.map) return false;
    if (typeof ol === 'undefined') return false;

    this.initVectorLayer();

    // Satellite layer (Google Maps satellite)
    this.satelliteLayer = new ol.layer.Tile({
      source: new ol.source.XYZ({
        url: 'https://mt{0-3}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
        attributions: '© Esri'
      }),
      opacity: 1,
      visible: true
    });

    // Plan layer (OpenStreetMap)
    this.planLayer = new ol.layer.Tile({
      source: new ol.source.OSM(),
      opacity: 1,
      visible: false
    });

    this.map = new ol.Map({
      target: this.mapContainer,
      layers: [this.satelliteLayer, this.planLayer, this.vectorLayer],
      view: new ol.View({
        center: ol.proj.fromLonLat(this.center),
        zoom: this.zoom
      })
    });

    this._isSatelliteLayer = true;
    return true;
  }

  updateCenterMarker(lon: number, lat: number, isCenterMarker: boolean): void {
    if (!this.map || typeof ol === 'undefined') return;

    const view = this.map.getView();
    view.animate({
      center: ol.proj.fromLonLat([lon, lat]),
      duration: 250
    });

    if (this.centerMarker) {
      this.vectorSource.removeFeature(this.centerMarker);
    }

    if (isCenterMarker) {
      const markerFeature = new ol.Feature({
        geometry: new ol.geom.Point(ol.proj.fromLonLat([lon, lat])),
        type: 'centerMarker'
      });

      markerFeature.setStyle(new ol.style.Style({
        image: new ol.style.Icon({
          anchor: [0.5, 1],
          anchorXUnits: 'fraction',
          anchorYUnits: 'fraction',
          src: 'data:image/svg+xml;base64,' + btoa(`
            <svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.5 0C5.596 0 0 5.596 0 12.5c0 2.902 1.004 5.57 2.68 7.682L12.5 41l9.82-20.818C23.996 18.07 25 15.402 25 12.5 25 5.596 19.404 0 12.5 0z" fill="#ff0000"/>
              <circle cx="12.5" cy="12.5" r="6" fill="white"/>
            </svg>
          `),
          scale: 1
        })
      }));

      this.vectorSource.addFeature(markerFeature);
      this.centerMarker = markerFeature;
    }
  }

  toggleSatelliteLayer(tile: 'plan' | 'satellite'): void {
    if (!this.map) return;

    if (tile === 'satellite') {
      this._isSatelliteLayer = true;
      this.satelliteLayer?.setVisible(true);
      this.planLayer?.setVisible(false);
    } else if (tile === 'plan') {
      this._isSatelliteLayer = false;
      this.satelliteLayer?.setVisible(false);
      this.planLayer?.setVisible(true);
    }
  }

  /** Switch base layer to the OSM plan or one of the satellite sources (Google/Sentinel/Landsat). */
  setBaseLayer(type: OldMapBaseLayer): void {
    if (!this.map || typeof ol === 'undefined') return;

    if (type === 'plan') {
      this._isSatelliteLayer = false;
      this.satelliteLayer?.setVisible(false);
      this.planLayer?.setVisible(true);
      return;
    }

    const url = OLD_MAP_SATELLITE_URLS[type];
    if (this.satelliteLayer && url) {
      this.satelliteLayer.setSource(
        new ol.source.XYZ({
          url,
          crossOrigin: 'anonymous',
        }),
      );
    }
    this._isSatelliteLayer = true;
    this.satelliteLayer?.setVisible(true);
    this.planLayer?.setVisible(false);
  }

  zoomIn(): void {
    if (this.map) {
      const view = this.map.getView();
      const zoom = view.getZoom();
      view.animate({ zoom: zoom + 1, duration: 250 });
    }
  }

  zoomOut(): void {
    if (this.map) {
      const view = this.map.getView();
      const zoom = view.getZoom();
      view.animate({ zoom: zoom - 1, duration: 250 });
    }
  }

  centerMap(center: [number, number], zoom: number): void {
    if (this.map && center) {
      const view = this.map.getView();
      view.animate({
        center: ol.proj.fromLonLat(center),
        zoom: zoom,
        duration: 250
      });
    }
  }

  fitToExtent(extent: any, options?: any): void {
    if (this.map && extent && !extent.some((val: any) => !isFinite(val))) {
      this.map.getView().fit(extent, options || {
        padding: [50, 50, 50, 50],
        maxZoom: 17
      });
    }
  }

  addInteraction(interaction: any): void {
    this.map?.addInteraction(interaction);
  }

  removeInteraction(interaction: any): void {
    this.map?.removeInteraction(interaction);
  }

  addLayer(layer: any): void {
    this.map?.addLayer(layer);
  }

  /**
   * Insert a raster layer right above the base tile layers (satellite/plan) but below
   * the vector layers, so polygon outlines stay on top of the index tiles.
   */
  addIndexTileLayer(layer: any): void {
    if (!this.map) return;
    const layers = this.map.getLayers();
    const arr = layers.getArray();
    let idx = arr.findIndex((l: any) => !(l instanceof ol.layer.Tile));
    if (idx < 0) idx = arr.length;
    layers.insertAt(idx, layer);
  }

  removeLayer(layer: any): void {
    this.map?.removeLayer(layer);
  }

  getSize():any {
    return this.map.getSize();
  }

  getView():any {
    return this.map.getView();
  }

  on(event: string, handler: (evt: any) => void): void {
    this.map?.on(event, handler);
  }

  un(event: string, handler: (evt: any) => void): void {
    this.map?.un(event, handler);
  }

  destroy(): void {
    if (this.map) {
      this.map.setTarget(null);
      this.map = null;
    }
  }
}

